import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { getClinicSpecialists, getClinic, getSpecialistSlots } from '../api/client';
import Footer from '../components/Footer';

export default function SpecialistsPage() {
  const { clinicId, serviceId } = useParams();
  const navigate = useNavigate();
  const [specialists, setSpecialists] = useState([]);
  const [clinic, setClinic] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [slotsBySpecialist, setSlotsBySpecialist] = useState({});

  useEffect(() => {
    async function fetchData() {
      try {
        setLoading(true);
        const [specialistsResponse, clinicResponse] = await Promise.all([
          getClinicSpecialists(clinicId),
          getClinic(clinicId)
        ]);
        
        const specialistsList = specialistsResponse.specialists || [];
        setSpecialists(specialistsList);
        setClinic(clinicResponse.clinic);

        // Получаем слоты для каждого специалиста
        const today = new Date().toISOString().split('T')[0];
        const slotsPromises = specialistsList.map(specialist => 
          getSpecialistSlots(specialist.id, today)
            .then(response => ({
              specialistId: specialist.id,
              slots: response.availableSlots[today] || []
            }))
            .catch(() => ({
              specialistId: specialist.id,
              slots: []
            }))
        );

        const slotsResults = await Promise.all(slotsPromises);
        const slotsMap = slotsResults.reduce((acc, { specialistId, slots }) => {
          acc[specialistId] = slots;
          return acc;
        }, {});
        
        setSlotsBySpecialist(slotsMap);
        setError(null);
      } catch (err) {
        console.error('Ошибка загрузки данных специалистов:', err);
        setError('Не удалось загрузить список специалистов. Пожалуйста, попробуйте позже.');
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [clinicId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-red-500 text-center">
          <p className="text-xl font-semibold mb-2">Ошибка</p>
          <p>{error}</p>
          <button 
            onClick={() => navigate(`/${clinicId}`)}
            className="mt-4 px-4 py-2 bg-secondary text-white rounded-md hover:bg-secondary/90 transition"
          >
            Вернуться к клинике
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 text-gray-800 relative pb-16">
      <header className="bg-white shadow p-4 sticky top-0 z-10">
        <div className="flex items-center gap-4 max-w-xl mx-auto relative">
          <Link 
            to={`/${clinicId}`} 
            className="absolute left-0 text-gray-600 hover:bg-gray-100 rounded-full w-10 h-10 flex items-center justify-center transition"
          >
            ←
          </Link>
          <div className="text-center flex-grow">
            <h1 className="text-lg font-semibold">Выбор специалиста</h1>
            {clinic && (
              <div className="text-sm text-gray-500">{clinic.name}</div>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-xl mx-auto px-4 py-6">
        {specialists.length === 0 ? (
          <div className="text-center text-gray-500 py-8">
            <p className="text-lg mb-2">Специалисты не найдены</p>
            <p className="text-sm">Пожалуйста, попробуйте выбрать другую услугу</p>
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            {specialists.map((s) => (
              <Link
                to={`/${clinicId}/${serviceId}/${s.id}`}
                key={s.id}
                className="bg-white rounded-xl shadow p-4 flex gap-4 hover:shadow-md transition"
              >
                <img
                  src={s.photo}
                  alt={s.name}
                  className="w-14 h-14 rounded-full object-cover bg-primary/10 border border-white shadow"
                />
                <div className="flex-grow">
                  <div className="font-medium text-base">{s.name}</div>
                  <div className="text-sm text-gray-500">{s.specialization}</div>
                  {s.specializations && s.specializations.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {s.specializations.map((spec, idx) => (
                        <span
                          key={idx}
                          className="text-xs bg-primary/10 text-primary px-2 py-1 rounded-full"
                        >
                          {spec}
                        </span>
                      ))}
                    </div>
                  )}
                  <div className="flex items-center text-sm text-gray-500 gap-4 mt-2">
                    <span>⭐ {typeof s.avarage_rating === 'number' ? s.avarage_rating.toFixed(1) : '5.0'}</span>
                    {s.experience && <span>Стаж: {s.experience} лет</span>}
                  </div>
                  {s.description && (
                    <div className="text-sm text-gray-500 mt-1 line-clamp-2">
                      {s.description}
                    </div>
                  )}
                  <div className="mt-3 pt-3 border-t border-gray-100">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <svg className="w-4 h-4 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span className="text-sm font-medium text-primary">
                          {slotsBySpecialist[s.id]?.length || 0} {slotsBySpecialist[s.id]?.length === 1 ? 'слот' : 
                            slotsBySpecialist[s.id]?.length < 5 ? 'слота' : 'слотов'} сегодня
                        </span>
                      </div>
                      <span className="text-sm text-gray-400">Записаться →</span>
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
